import {consoleLogger, IBrowser, IEventManagerWithAsync, PostMessageHandler} from '@gongxigames/framework/core';
import {AutoplayEvents, BottomPanelEvents, FlowModuleEvents, LogicEvents} from '@gongxigames/framework/slot';
import {SignalEventEmitterWrapper} from '@gongxigames/framework/core/services/strictEventEmitter';
import {strictUiEmitter, StrictUiEmitterEventMap} from '@gongxigames/framework/slotV2/globals';

export class PPClientSideMessages {
    public static readonly GAME_INITIALIZED: symbol = Symbol('GAME_INITIALIZED');
    public static readonly GAME_LOADED: symbol = Symbol('GAME_LOADED');
    public static readonly GAME_READY: symbol = Symbol('GAME_READY');
    // TODO: fix with https://jira.gongxigames.com/browse/CLFR-591
    public static readonly ROUND_ENDED: symbol = Symbol('ROUND_ENDED');
    public static readonly REVEAL_ALL: symbol = Symbol('REVEAL_ALL');
    public static readonly NO_ROUNDS_LEFT: symbol = Symbol('NO_ROUNDS_LEFT');
    public static readonly ROUND_START: symbol = Symbol('ROUND_START');
    public static readonly ROUND_STARTED: symbol = Symbol('ROUND_STARTED');
    public static readonly BALACE_UPDATED: symbol = Symbol('BALACE_UPDATED');
    public static readonly CASHIER_CLICKED: symbol = Symbol('CASHIER_CLICKED');
    public static readonly QUIT_GAME: symbol = Symbol('QUIT_GAME');
    public static readonly TICKET_RECEIVED: symbol = Symbol('TICKET_RECEIVED');
}

type PPMessageType = 'enableSpin' | 'disableSpin' | 'pause' | 'resume' | 'stopAutobet';
type RoundEndedData = {
    win: number;
    balanceAfter: number;
    ticketsLeftAfter: number;
};
type RoundStartData = {
    totalBet: number;
};
type RoundStartedData = {
    balanceBefore: number;
    ticketsLeftBefore: number;
};
type BalanceData = {
    amount: number;
};

type BetsData = {
    MinBetAmount: number;
    CurrentBetAmount: number;
};

// do not change message names as they are listening on Pariplay side
export type G2WClientSideMessages = {
    'onAppFrameReady': void;
    'gameDataLoaded': void;
    'onGameLoad': void;
    'gameReady': void;
    'roundEnded': RoundEndedData;
    'roundStart': RoundStartData;
    'roundStarted': RoundStartedData;
    'balance': BalanceData;
    'balanceUpdate': BalanceData;
    'cashier': void;
    'quit': void;
    'fullscreen': boolean;
    'ticketReceived': void;
    'minBetAmountMessage': BetsData;
};

export type G2WClientSideMessagesMap = { [key in keyof G2WClientSideMessages]: G2WClientSideMessages[key] };

export class PPClientSideMessagesHandler extends PostMessageHandler<G2WClientSideMessagesMap> {
    static NAME = 'PPClientSideMessagesHandler';

    private receiveMessageListener?: (e: MessageEvent) => void;

    constructor(
        eventManager: IEventManagerWithAsync,
        uiEmitter: SignalEventEmitterWrapper<StrictUiEmitterEventMap>,
        browser: IBrowser,
        gameCode: string,
        languageCode: string
    ) {
        super(eventManager, uiEmitter, browser, gameCode, languageCode);

        this.postMessage('onAppFrameReady');
    }

    protected setupEvents(): void {
        this._eventManager.on('General.Game.INITIALIZED', this.onGameReady.bind(this), this);
        this._eventManager.on('General.Game.SOFT_RELOAD', this.removeListeners.bind(this), this);
        this._eventManager.on(PPClientSideMessages.GAME_LOADED, this.onGameLoaded.bind(this), this);
        this._eventManager.on(FlowModuleEvents.ROUND_ENDED, this.onRoundEnded.bind(this), this);
        this._eventManager.on(LogicEvents.ROUND_START, this.onRoundStart.bind(this), this);
        this._eventManager.on(LogicEvents.ROUND_STARTED, this.onRoundStarted.bind(this), this);
        this._eventManager.on(LogicEvents.BALANCE_UPDATED, this.onBalanceUpdated.bind(this), this);
        this._eventManager.on(PPClientSideMessages.CASHIER_CLICKED, this.onCashierClicked.bind(this), this);
        this._eventManager.on(PPClientSideMessages.QUIT_GAME, this.onQuitGame.bind(this), this);
        this._eventManager.on(PPClientSideMessages.TICKET_RECEIVED, this.onTicketReceived.bind(this), this);
        this._eventManager.on(BottomPanelEvents.FULLSCREEN_CLICKED, this.onFullscreenClicked.bind(this), this);
        strictUiEmitter.on('UI.command.fullScreen', this.onFullscreenClicked.bind(this));
    }

    protected setupListeners(): void {
        if (!this.receiveMessageListener) {
            this.receiveMessageListener = this.receiveMessage.bind(this);
        }

        this._browser.addEventListener('message', this.receiveMessageListener, false);
    }

    protected removeListeners(): void {
        if (this.receiveMessageListener) {
            this._browser.removeEventListener('message', this.receiveMessageListener, false);
        }
    }

    protected receiveMessage(event: MessageEvent): void {
        const {data} = event;

        if (data && data.type) {
            const messageId = data.type as PPMessageType;

            switch (messageId) {
                case 'enableSpin': {
                    // this._eventManager.emit(SpinButtonEvents.ENABLE, PPClientSideMessagesHandler.NAME);
                    this._uiEmitter.emit('Integration.enableSpinButton');
                    break;
                }
                case 'disableSpin': {
                    // this._eventManager.emit(SpinButtonEvents.DISABLE, PPClientSideMessagesHandler.NAME);
                    this._uiEmitter.emit('Integration.disableSpinButton');
                    break;
                }
                case 'pause': {
                    this.pauseGame();
                    break;
                }
                case 'resume': {
                    this.unpauseGame();
                    break;
                }
                case 'stopAutobet': {
                    this.stopAutobet();
                    break;
                }
                default: {
                    consoleLogger.warn('Message is not supported', data);
                    break;
                }
            }
        } else {
            consoleLogger.warn('Error occurred or such message is not supported', data);
        }
    }

    protected pauseGame(): void {
        this.disableGameUI();
        this._eventManager.emitAsync('General.System.RESUME');
    }

    protected unpauseGame(): void {
        this.enableGameUI();
        this._eventManager.emitAsync('General.System.RESUME');
    }

    protected disableGameUI(): void {
        this._eventManager.emit('General.UI.DISABLE', PPClientSideMessagesHandler.NAME);
        this._eventManager.emit('Autoplay.StopButton.DISABLE', PPClientSideMessagesHandler.NAME);
    }

    protected enableGameUI(): void {
        this._eventManager.emit('General.UI.ENABLE', PPClientSideMessagesHandler.NAME);
        this._eventManager.emit('Autoplay.StopButton.ENABLE', PPClientSideMessagesHandler.NAME);
    }

    protected stopAutobet(): void {
        this._eventManager.emit(AutoplayEvents.FORCE_STOP_AUTOPLAY);
    }

    protected onGameLoaded(): void {
        this.postMessage('gameDataLoaded');
    }

    protected onGameReady(): void {
        this.postMessage('gameReady');
    }

    public onNonMinBetPopup(data: BetsData): void {
        this.postMessageWithDataToExtract('minBetAmountMessage', data);
    }

    protected onRoundEnded(data: RoundEndedData): void {
        this.postMessage('roundEnded', data);
    }

    protected onRoundStart(totalBet: number): void {
        this.postMessage('roundStart', {totalBet});
    }

    protected onRoundStarted(data: RoundStartedData): void {
        this.postMessage('roundStarted', data);
    }

    protected onBalanceUpdated(amount: number): void {
        this.postMessage('balance', {amount});
    }

    protected onCashierClicked(): void {
        this.postMessage('cashier');
    }

    protected onFullscreenClicked(isFullscreen: boolean): void {
        this.postMessage('fullscreen', isFullscreen);
    }

    protected onQuitGame(): void {
        this.postMessage('quit');
    }

    protected onTicketReceived(data: any): void {
        this.postMessage('ticketReceived', data);
    }
}
